import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, X, Users2, RotateCcw } from "lucide-react";
import { useState } from "react";
import {
  defaultAdvancedFilters,
  type AdvancedFilters,
  type IncludeExclude,
  type RangeNum,
  type TriState,
  type NameDup,
} from "./userFilters";

export { defaultAdvancedFilters };
export type { AdvancedFilters };

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  value: AdvancedFilters;
  onChange: (v: AdvancedFilters) => void;
}

export function AdvancedFilterModal({ open, onOpenChange, value, onChange }: Props) {
  const [draft, setDraft] = useState<AdvancedFilters>(value);

  // Sync when modal opens
  const handleOpen = (o: boolean) => {
    if (o) setDraft(value);
    onOpenChange(o);
  };

  const apply = () => {
    onChange(draft);
    onOpenChange(false);
  };

  const reset = () => {
    setDraft(defaultAdvancedFilters);
  };

  const update = <K extends keyof AdvancedFilters>(key: K, val: AdvancedFilters[K]) => {
    setDraft((d) => ({ ...d, [key]: val }));
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Avancerat filter</DialogTitle>
          <DialogDescription>
            Bygg en exakt sökning. Fält kombineras med OCH, värden inom samma fält med ELLER.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 text-sm">
          <IncludeExcludeRow
            label="Kontotyp"
            options={[
              { value: "personal", label: "Personlig" },
              { value: "company", label: "Företag" },
              { value: "admin", label: "Admin" },
            ]}
            value={draft.accountType}
            onChange={(v) => update("accountType", v as any)}
          />
          <IncludeExcludeRow
            label="Kontostatus"
            options={[
              { value: "active", label: "Aktiv" },
              { value: "restricted", label: "Begränsad" },
              { value: "pending", label: "Väntar verifiering" },
              { value: "deleted", label: "Raderad" },
            ]}
            value={draft.accountStatus}
            onChange={(v) => update("accountStatus", v as any)}
          />
          <IncludeExcludeRow
            label="Verifieringsstatus"
            options={[
              { value: "verified", label: "Verifierad" },
              { value: "unverified", label: "Ej verifierad" },
              { value: "pending", label: "Väntande" },
            ]}
            value={draft.verificationStatus}
            onChange={(v) => update("verificationStatus", v as any)}
          />

          <Separator />

          <FreeTextIncludeExclude
            label="Län"
            placeholder="t.ex. Stockholm"
            value={draft.province}
            onChange={(v) => update("province", v)}
          />
          <FreeTextIncludeExclude
            label="Kommun"
            placeholder="t.ex. Uppsala"
            value={draft.municipality}
            onChange={(v) => update("municipality", v)}
          />

          <Separator />

          <div>
            <Label className="text-xs uppercase text-muted-foreground tracking-wide">
              Registreringsdatum
            </Label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Från</Label>
                <Input
                  type="date"
                  value={draft.registeredFrom}
                  onChange={(e) => update("registeredFrom", e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">Till</Label>
                <Input
                  type="date"
                  value={draft.registeredTo}
                  onChange={(e) => update("registeredTo", e.target.value)}
                />
              </div>
            </div>
          </div>

          <Separator />

          <RangeRow
            label="Aktiva annonser"
            value={draft.activeListings}
            onChange={(v) => update("activeListings", v)}
          />
          <RangeRow
            label="Antal varningar"
            value={draft.warnings}
            onChange={(v) => update("warnings", v)}
            hint="Varningssystem ej aktivt – alla användare har 0"
          />
          <RangeRow
            label="Avslutade köp"
            value={draft.completedPurchases}
            onChange={(v) => update("completedPurchases", v)}
          />
          <RangeRow
            label="Antal mottagna omdömen"
            value={draft.reviewsCount}
            onChange={(v) => update("reviewsCount", v)}
          />
          <RangeRow
            label="Snittbetyg"
            value={draft.avgRating}
            onChange={(v) => update("avgRating", v)}
            step="0.1"
            hintMaxValue={5}
          />

          <Separator />

          <TriStateRow
            label="Kopplad social media"
            value={draft.hasSocial}
            onChange={(v) => update("hasSocial", v)}
          />
          <TriStateRow
            label="Har omslagsbild"
            value={draft.hasCover}
            onChange={(v) => update("hasCover", v)}
          />
          <TriStateRow
            label="Har profilbild"
            value={draft.hasAvatar}
            onChange={(v) => update("hasAvatar", v)}
          />

          <Separator />

          {/* Duplicate detection */}
          <section className="rounded-lg border-2 border-dashed border-amber-300 dark:border-amber-700 bg-amber-50/40 dark:bg-amber-950/20 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Users2 className="h-4 w-4 text-amber-700 dark:text-amber-300" />
              <h3 className="font-semibold text-amber-900 dark:text-amber-100">
                Hitta dubbletter
              </h3>
            </div>
            <p className="text-xs text-amber-800/80 dark:text-amber-200/80 mb-3">
              Gruppera konton som delar samma värde i ett eller flera fält. Aktivera
              vilka fält som ska kontrolleras.
            </p>
            <div className="space-y-2">
              <DupCheck
                label="E-postadress"
                checked={draft.duplicates.email}
                onChange={(v) =>
                  update("duplicates", { ...draft.duplicates, email: v })
                }
              />
              <DupCheck
                label="Telefonnummer"
                checked={draft.duplicates.phone}
                onChange={(v) =>
                  update("duplicates", { ...draft.duplicates, phone: v })
                }
              />
              <DupCheck
                label="Organisationsnummer"
                checked={draft.duplicates.organization_number}
                onChange={(v) =>
                  update("duplicates", {
                    ...draft.duplicates,
                    organization_number: v,
                  })
                }
              />
              <div className="flex items-center justify-between gap-2 py-1">
                <Label className="text-sm">Namn</Label>
                <Select
                  value={draft.duplicates.name}
                  onValueChange={(v) =>
                    update("duplicates", { ...draft.duplicates, name: v as NameDup })
                  }
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[1100]">
                    <SelectItem value="off">Av</SelectItem>
                    <SelectItem value="exact">Exakt matchning</SelectItem>
                    <SelectItem value="fuzzy">Ungefärlig matchning</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DupCheck
                label="Kommun + namn"
                checked={draft.duplicates.kommun_name}
                onChange={(v) =>
                  update("duplicates", { ...draft.duplicates, kommun_name: v })
                }
              />
            </div>
          </section>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" onClick={reset}>
            <RotateCcw className="mr-2 h-4 w-4" />
            Rensa filter
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Avbryt
            </Button>
            <Button onClick={apply}>Tillämpa</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- subcomponents ---

interface Opt {
  value: string;
  label: string;
}

function IncludeExcludeRow({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: Opt[];
  value: IncludeExclude<string>;
  onChange: (v: IncludeExclude<string>) => void;
}) {
  const toggle = (kind: "include" | "exclude", v: string) => {
    const list = value[kind];
    const otherKind = kind === "include" ? "exclude" : "include";
    const isOn = list.includes(v);
    const next: IncludeExclude<string> = {
      ...value,
      [kind]: isOn ? list.filter((x) => x !== v) : [...list, v],
      // Ensure value is not in both lists
      [otherKind]: value[otherKind].filter((x) => x !== v),
    };
    onChange(next);
  };

  return (
    <div>
      <Label className="text-xs uppercase text-muted-foreground tracking-wide">
        {label}
      </Label>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {options.map((opt) => {
          const inc = value.include.includes(opt.value);
          const exc = value.exclude.includes(opt.value);
          return (
            <div key={opt.value} className="inline-flex rounded-md border border-border overflow-hidden">
              <button
                type="button"
                onClick={() => toggle("include", opt.value)}
                className={`px-2.5 py-1 text-xs transition-colors ${
                  inc
                    ? "bg-green-600 text-white"
                    : "hover:bg-accent text-muted-foreground"
                }`}
                aria-pressed={inc}
                title="Inkludera"
              >
                <Plus className="inline h-3 w-3 mr-1" />
                {opt.label}
              </button>
              <button
                type="button"
                onClick={() => toggle("exclude", opt.value)}
                className={`px-2 py-1 text-xs border-l border-border transition-colors ${
                  exc
                    ? "bg-destructive text-destructive-foreground"
                    : "hover:bg-accent text-muted-foreground"
                }`}
                aria-pressed={exc}
                title="Exkludera"
              >
                <X className="inline h-3 w-3" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FreeTextIncludeExclude({
  label,
  placeholder,
  value,
  onChange,
}: {
  label: string;
  placeholder?: string;
  value: IncludeExclude<string>;
  onChange: (v: IncludeExclude<string>) => void;
}) {
  const [text, setText] = useState("");
  const [mode, setMode] = useState<"include" | "exclude">("include");

  const add = () => {
    const t = text.trim();
    if (!t) return;
    const otherKind = mode === "include" ? "exclude" : "include";
    if (value[mode].includes(t)) {
      setText("");
      return;
    }
    onChange({
      ...value,
      [mode]: [...value[mode], t],
      [otherKind]: value[otherKind].filter((x) => x !== t),
    });
    setText("");
  };

  const remove = (kind: "include" | "exclude", v: string) => {
    onChange({ ...value, [kind]: value[kind].filter((x) => x !== v) });
  };

  return (
    <div>
      <Label className="text-xs uppercase text-muted-foreground tracking-wide">
        {label}
      </Label>
      <div className="mt-2 flex gap-1.5">
        <Select value={mode} onValueChange={(v) => setMode(v as "include" | "exclude")}>
          <SelectTrigger className="w-[120px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="z-[1100]">
            <SelectItem value="include">Inkludera</SelectItem>
            <SelectItem value="exclude">Exkludera</SelectItem>
          </SelectContent>
        </Select>
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder={placeholder}
        />
        <Button type="button" variant="outline" onClick={add}>
          Lägg till
        </Button>
      </div>
      {(value.include.length > 0 || value.exclude.length > 0) && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {value.include.map((v) => (
            <Badge key={`i-${v}`} className="bg-green-600 hover:bg-green-700 text-white gap-1">
              <Plus className="h-3 w-3" />
              {v}
              <button onClick={() => remove("include", v)} aria-label="Ta bort">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {value.exclude.map((v) => (
            <Badge key={`e-${v}`} variant="destructive" className="gap-1">
              <X className="h-3 w-3" />
              {v}
              <button onClick={() => remove("exclude", v)} aria-label="Ta bort">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

function RangeRow({
  label,
  value,
  onChange,
  step,
  hint,
  hintMaxValue,
}: {
  label: string;
  value: RangeNum;
  onChange: (v: RangeNum) => void;
  step?: string;
  hint?: string;
  hintMaxValue?: number;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <Label className="text-xs uppercase text-muted-foreground tracking-wide">
          {label}
        </Label>
        {hintMaxValue !== undefined && (
          <span className="text-[10px] text-muted-foreground">max {hintMaxValue}</span>
        )}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <Input
          type="number"
          step={step}
          placeholder="Min"
          value={value.min}
          onChange={(e) =>
            onChange({ ...value, min: e.target.value === "" ? "" : Number(e.target.value) })
          }
        />
        <Input
          type="number"
          step={step}
          placeholder="Max"
          value={value.max}
          onChange={(e) =>
            onChange({ ...value, max: e.target.value === "" ? "" : Number(e.target.value) })
          }
        />
      </div>
      {hint && <p className="mt-1 text-[10px] text-muted-foreground italic">{hint}</p>}
    </div>
  );
}

function TriStateRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: TriState;
  onChange: (v: TriState) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <Label className="text-sm">{label}</Label>
      <div className="inline-flex rounded-md border border-border overflow-hidden">
        {([
          { v: "any", label: "Alla" },
          { v: "yes", label: "Ja" },
          { v: "no", label: "Nej" },
        ] as { v: TriState; label: string }[]).map((opt, i) => (
          <button
            key={opt.v}
            type="button"
            onClick={() => onChange(opt.v)}
            className={`px-3 py-1 text-xs transition-colors ${i > 0 ? "border-l border-border" : ""} ${
              value === opt.v ? "bg-primary text-primary-foreground" : "hover:bg-accent"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function DupCheck({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 py-1 cursor-pointer">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} />
      <span className="text-sm">{label}</span>
    </label>
  );
}
